import {
  addMinutes,
  differenceInMinutes,
  getHours,
  getMinutes,
  isBefore,
  isSameMinute,
  Locale,
} from "date-fns";
// import * as Locales from "date-fns/locale";

import {
  differenceInMilliseconds,
  addMilliseconds,
  startOfToday,
  endOfToday,
  startOfTomorrow,
  differenceInHours,
  addDays,
  format,
} from "date-fns";
import { NotificationType } from "../controllers/messaging";
import Settings from "../controllers/settings";
import StatefulSettings from "../controllers/StatefulSettings";
import {
  AuditMessage,
  defaultSodEvent,
  EventItem,
  IEventItem,
  IPermissionItem,
  IScheduleOptions,
  ISchedulerTimerInfo,
  IShowNotificationState,
  ITaskItem,
} from "../ts/Definitions";
import {
  AuditActionTypes,
  AvailabilityType,
  EventDay,
  EventType,
  ItemTypes,
  JavascriptDay,
  TraceLevel,
} from "../ts/enums";
import Emitter from "./Emitter";
import { getRandom, getRandomCountForItem, isTimedItem } from "./utils";
import round from "lodash/round";
import enUs from "date-fns/locale/en-US";
import { now } from "lodash";

const eodOffset = 4;
const lastScheduleOffset = 8;
export const defaultDayLengthHours = 18;

async function loadLocale(locale: string) {
  // Convert to correct format: en-US, fr, etc.
  // let code = locale;
  // if (code.length === 2) {
  //   // e.g. 'en' -> 'en-US'
  //   code = code + '-US';
  // }
  // try {
  //   return (await import(`date-fns/locale/${code}`));
  // } catch {
  //   // fallback to 'en-US'
  return enUs;
  // }
}
/**
 * Looks up a date-fns locale from the Expo localization object.  This falls back to `en-US`
 * @param localization Expo Localization object containing the locale and region.
 * @returns date-fns locale.
 */
export async function getDateFnsLocale(): Promise<Locale> {
  let localeString = navigator.language || 'en-US';
  // Always use dash format
  localeString = localeString.replace('_', '-');
  try {
    return await loadLocale(localeString);
  } catch {
    return await loadLocale('en-US');
  }
}
/*
    maybe this should be a container that holds state of date overrides and info for the day? or 
    is it ok to have static methods. all date manipulation and calculations when determining times 
    in a setup of a schedule should be in here i think
*/

export const getScheduleTime = (
  settings: StatefulSettings,
  day: EventDay,
  item: IEventItem | IPermissionItem
): Date => {
  const range = getScheduleTimeRange(settings, day, item);
  return getRandomTimeFromDate(range.minDate, range.maxDate, settings);
};

// this is run every morning when we are scheduling the day and planning the times
// we are going to update the sod and eod times on the events based on the new overrides available
// work out when the cut off is presently for the last task of the day to be applied and update that too based on the ratio of where it sits between sod and eod
// now let's determine the ratio of the the current times compared to the start and end time
// now we know roughly how far during the day the times are set
// we can now look at the new scale for the day and determine how much more compact or expanded it is
// for each event, work out where in the scale it sits with the new times scale using the ratio where it resides
// so if we have an
// sod at 4am
// event 1 as 8am (25%)
// event 2 at 12am (50%)
// event 3 at 4pm (75%)
// eod at 8pm
// then we compress the scale to 8am-4pm
// the events look like this
// event 1 10am
// event 2 12am
// event 3 2pm
const getScheduleTimeRange = (
  settings: StatefulSettings,
  day: EventDay,
  item: IEventItem | IPermissionItem
): { minDate: Date; maxDate: Date } => {
  let { minTime, maxTime } = item;

  if (!minTime) minTime = nowAsTimeString();
  if (!maxTime && item.itemType === ItemTypes.permission)
    maxTime = getEndOfDayOffsetTime(settings);
  let defaultMinTime = parseTimeString(minTime, settings);
  if (isNaN(defaultMinTime)) {
    minTime = getSODStartTime(settings);
    defaultMinTime = parseTimeString(minTime, settings);
  }
  let defaultMaxTime = parseTimeString(maxTime as string, settings, false);
  if (isNaN(defaultMaxTime)) {
    defaultMaxTime = defaultMinTime;
    maxTime = minTime;
  }

  const defaultTimes = {
    minDate: addMilliseconds(startOfToday(), defaultMinTime),
    maxDate: addMilliseconds(startOfToday(), defaultMaxTime),
  };

  const offsets = settings.getOverrideTimes();
  if (!offsets || !offsets[day]) return defaultTimes;

  const currentHour = getSODHour(settings);
  if (currentHour === undefined) return defaultTimes;

  const dayOffset = offsets[day];
  const newMin = dayOffset[0];
  const newMax = dayOffset[1];

  // Compare against the true defaults used by getDayHours (SOD hour → SOD + 18h),
  // NOT getEndOfDayOffset which returns a wrap-around midnight value.
  const defaultMin = currentHour;
  const defaultMax = currentHour + defaultDayLengthHours;
  if (newMin === defaultMin && newMax === defaultMax) {
    return defaultTimes; // override matches system defaults – no compression needed
  }

  const currentStartTime = getSODStartTime(settings);
  const sodDate = addMilliseconds(
    startOfToday(),
    parseTimeString(currentStartTime, settings)
  );
  let eodDate = addMilliseconds(
    startOfToday(),
    parseTimeString(getEndOfDayOffsetTime(settings), settings)
  );
  if (eodDate < sodDate) eodDate = addDays(eodDate, 1);
  const minutesInDay = differenceInMinutes(eodDate, sodDate);
  const newSODTime = addMilliseconds(
    startOfToday(),
    parseTimeString(formatHourToTimeString(newMin), settings)
  );
  let newEODTime = addMilliseconds(
    startOfToday(),
    parseTimeString(formatHourToTimeString(newMax), settings)
  );
  if (newEODTime < newSODTime) newEODTime = addDays(newEODTime, 1);
  const newMinutesInDay = differenceInMinutes(newEODTime, newSODTime);
  let maxDate = eodDate;
  let minDate = sodDate;
  let minRatio = 0; // how far from sod we are with min time
  let maxRatio = 0; // how far we are from eod with our max time
  if (maxTime) {
    maxDate = addMilliseconds(
      startOfToday(),
      parseTimeString(maxTime, settings)
    );
    const diff = differenceInMinutes(eodDate, maxDate);
    if (diff > 0) maxRatio = diff / minutesInDay;
  }
  if (minTime) {
    minDate = addMilliseconds(
      startOfToday(),
      parseTimeString(minTime, settings)
    );
    const diff = differenceInMinutes(minDate, sodDate);
    if (diff > 0) minRatio = diff / minutesInDay;
  }
  if (minRatio || maxRatio) {
    minDate = addMinutes(newSODTime, minRatio * newMinutesInDay);
    if (maxRatio)
      maxDate = addMinutes(newEODTime, maxRatio * newMinutesInDay * -1);
    if (maxDate < minDate) maxDate = minDate;
  }

  //allows us to ensure the events always have a window to apply - todpo: might impact exact timed custom events
  if (!(minDate < maxDate)) {
    maxDate = addMinutes(maxDate, 30);
  }

  return { minDate, maxDate };
};


const nowAsTimeString = () => {
  const time = new Date();
  return `${time.getHours()}:${time.getMinutes()}`;
};

export const formatTime = (str: number): string => {
  if (str > 23) str = str - 24;
  if (str === 0) return '12 am';  // midnight
  if (str === 12) return '12 pm'; // noon
  if (str < 12) return `${str} am`;
  return str - 12 + " pm";
};

export const formatTimes = (numbers: number[]): string => {
  return `${formatTime(numbers[0])} - ${formatTime(numbers[1])}`;
};

const getEndOfDayOffset = (settings: Settings): number => {
  const sodTime = getSODHour(settings);
  if (sodTime < eodOffset) return 24 - eodOffset + sodTime;
  return sodTime - eodOffset;
};

export const getEndOfDayOffsetTime = (settings: Settings): string => {
  const eodHour = getEndOfDayOffset(settings);
  return formatHourToTimeString(eodHour);
};


const getLastScheduleOffsetForDay = (
  settings: StatefulSettings,
  day: EventDay
): number => {
  const sodTime = getSODHour(settings);
  let offset = lastScheduleOffset;
  const taskSettings = settings.getTaskSettings();
  if (taskSettings.maxScheduleTime) {
    const override = addMilliseconds(
      startOfToday(),
      parseTimeString(taskSettings.maxScheduleTime, settings)
    );
    const sod = addMilliseconds(
      startOfToday(),
      parseTimeString(getSODStartTime(settings), settings)
    );
    offset = differenceInHours(override, sod);
  }

  const offsets = settings.getOverrideTimes();
  if (offsets && offsets[day]) {
    const min = offsets[day][0];
    const max = offsets[day][1];
    const diff = min < max ? max - min : max + 24 - min;
    if (diff > lastScheduleOffset)
      offset = round(diff / (24 / lastScheduleOffset));
    else offset = 1;
    return max - offset;
  }

  if (sodTime < offset) return 24 - offset + sodTime;
  return sodTime - offset;
};

export const getLastScheduleOffsetTimeForDay = (
  settings: StatefulSettings,
  day: EventDay
): string => {
  const lastScheduleHour = getLastScheduleOffsetForDay(settings, day);
  return formatHourToTimeString(lastScheduleHour);
};

const formatHourToTimeString = (time: number) => {
  if (time > 23) {
    time = time - 24; // does this need to add a day?!
  }
  return time < 10 ? `0${time}:00` : `${time}:00`;
};

const formatHhMmToTimeString = (hour: number, minute: number) => {
  return `${hour < 10 ? "0" + hour : hour}:${minute < 10 ? "0" + minute : minute
    }`;
};

export const getSODHour = (settings: Settings): number => {
  const currentStartTime = getSODStartTime(settings);
  return getHourFromTimeString(currentStartTime);
};

const getHourFromTimeString = (timeString: string | undefined): number => {
  if (!timeString) return 0;
  return parseInt(timeString.split(":")[0]);
};

const getSODEvent = (settings: Settings) => {
  let sodEvent = settings.getStartOfDayEvent?.();
  if (!sodEvent) sodEvent = { ...defaultSodEvent } as EventItem;
  return sodEvent;
};

export const getSODStartTime = (settings: Settings): string => {
  const sodEvent = getSODEvent(settings);
  let currentStartTime = defaultSodEvent.minTime as string;
  if (sodEvent.minTime) currentStartTime = sodEvent.minTime;
  return currentStartTime;
};

// something here is quite wrong!
export const updateEventSettingsBasedOnNewSOD = (
  newHour: number,
  settings: Settings
): void => {
  const sodEvent = getSODEvent(settings);
  const currentStartTime = getSODStartTime(settings);
  const currentHour = parseInt(currentStartTime.split(":")[0]);
  if (currentHour === undefined || currentHour === newHour) return;
  const offSet = newHour - currentHour;
  // this is run whenever sod is updated
  sodEvent.minTime = `${newHour}:00`;
  sodEvent.maxTime = `${newHour}:00`;
  // this one is different - it also means we do want to store the new SOD when they change it on the diary page
  // we want to update the actual event times on the settings object based on where it sits in relation to sod and eod
  // updating sod pushes out the eod by the same amount
  // this doesn't compress or expand any time scales, just moves the point of reference to the right time of the day
  const events = settings.getEvents();
  let n = 0;
  events.forEach((event) => {
    if (event.key === sodEvent.key) return;
    const { minTime, maxTime } = event;
    if (!minTime && !maxTime) return;
    if (minTime) {
      event.minTime = updateTimeWithOffset(minTime, offSet, settings);
    }
    if (maxTime) {
      event.maxTime = updateTimeWithOffset(maxTime, offSet, settings);
    }
    n += 1;
    settings.updateSettingsItem(event, false, true);
  });
  Emitter.emitNotification({
    message: `Successfully updated ${n} events with new start times`,
    notificationType: NotificationType.SettingsInfo,
  });

  const perms = settings.getPermissions();
  n = 0;
  perms.forEach((perm) => {
    const { minTime, maxTime } = perm;
    if (!minTime && !maxTime) return;
    if (minTime) {
      perm.minTime = updateTimeWithOffset(minTime, offSet, settings);
    }
    if (maxTime) {
      perm.maxTime = updateTimeWithOffset(maxTime, offSet, settings);
    }
    n += 1;
    settings.updateSettingsItem(perm, false, true);
  });
  Emitter.emitNotification({
    message: `Successfully updated ${n} permissions with new start times`,
    notificationType: NotificationType.SettingsInfo,
  });

  const generalSettings = settings.getGeneralSettings();
  if (generalSettings.minReportTime) {
    generalSettings.minReportTime = updateTimeWithOffset(
      generalSettings.minReportTime,
      offSet,
      settings
    );
  }
  if (generalSettings.maxReportTime) {
    generalSettings.maxReportTime = updateTimeWithOffset(
      generalSettings.maxReportTime,
      offSet,
      settings
    );
  }
  void settings.saveGeneralSettings(
    generalSettings,
    undefined,
    true,
    undefined
  );
};

const updateTimeWithOffset = (
  minTime: string,
  offSet: number,
  settings: Settings
): string => {
  let minHour = parseInt(minTime.split(":")[0]);
  if (!minHour) return getSODStartTime(settings);
  minHour += offSet;
  return formatHourToTimeString(minHour);
};

const getRandomTime = (
  settings: Settings,
  lowerStr?: string,
  upperStr?: string
): Date => {
  const lower = addMilliseconds(
    startOfToday(),
    parseTimeString(lowerStr || getSODStartTime(settings), settings)
  );
  const upper = addMilliseconds(
    startOfToday(),
    parseTimeString(upperStr || getEndOfDayOffsetTime(settings), settings)
  );
  return getRandomTimeFromDate(lower, upper, settings);
};

const getRandomTimeFromDate = (
  minTime: Date,
  maxTime: Date,
  settings: Settings
): Date => {
  let lower = minTime;
  const upper = maxTime;
  const nowTime = addMilliseconds(
    startOfToday(),
    parseTimeString(
      new Date().getHours() + ":" + new Date().getMinutes(),
      settings
    )
  );
  if (lower < nowTime && upper < nowTime) {
    return nowTime; // means the window has passed for today so schedule immeditately
  }
  if (lower < nowTime && upper > nowTime) lower = nowTime;
  const lowerTicks = differenceInMilliseconds(lower, new Date(0)); // determing ms from the time as ticks against year dot
  const upperTicks = differenceInMilliseconds(upper, new Date(0)); // determing ms from the time as ticks against year dot
  const timeTicks = getRandom(
    0,
    differenceInMilliseconds(upperTicks, lowerTicks)
  );
  if (timeTicks < 0) return addMilliseconds(startOfToday(), lowerTicks); // means it should have fired already and is in the past.
  const date = addMilliseconds(lowerTicks, timeTicks);
  return date;
};

export const convertToMinutes = (n: number): string => {
  const num = n;
  const minutes = num / 60;
  const rminutes = Math.floor(minutes);
  const seconds = (minutes - rminutes) * 60;
  if (rminutes === 0) return Math.round(seconds) + " secs";
  return rminutes + " mins " + Math.round(seconds) + " secs";
};

export const getNextEventDateTime = (
  e: IEventItem | undefined,
  schedule: ISchedulerTimerInfo[],
  settings: Settings
): Date => {
  let nextSODDate = endOfToday(); // default the next time the event will fire to midnight
  if (!e || !e.minTime) return nextSODDate; // if we don't have a time range, return the default
  if (schedule) {
    // if we have a schedule
    const match = schedule.find((i) => i.key === e.key); // and we have the event scheduled
    if (match && match.startTime) return match.startTime; // return the time it will start
  }
  const nextTimeTicks = parseTimeString(e.minTime, settings); // otherwise, not yet scheduled, so best guess by using min time
  const timeTicks = differenceInMilliseconds(nextTimeTicks, new Date(0)); // determing ms from the time as ticks against year dot
  nextSODDate = addMilliseconds(startOfTomorrow(), timeTicks); // add the milliseconds to tomorrow as a good next guess
  return nextSODDate; // return the computed time
};

export const roundTimeDiff = (date: Date, compareDate: Date): string => {
  const hours = differenceInHours(compareDate, date);
  if (hours > 23) {
    const days = hours / 24;
    if (days > 6) {
      const weeks = days / 7;
      if (weeks > 51) {
        const years = weeks / 52;
        return `${Math.round(years)}y`;
      }
      return `${Math.round(weeks)}w`;
    }
    return `${Math.round(days)}d`;
  }
  if (hours >= 1) return `${Math.round(hours)}h`;
  return "<1h";
};

export function getTimeMinuteAfter(minTime: string, increment: number = 1): string {
  const [h, m] = minTime.split(":").map(Number);
  const date = new Date();
  date.setHours(h, m, 0, 0);
  date.setMinutes(date.getMinutes() + increment);
  return date.toTimeString().slice(0, 5);
}

export const asDate = (minTime: string): Date => {
  const [eventHour, eventMinute] = minTime.split(":").map(Number);
  const eventDate = new Date();
  eventDate.setHours(eventHour, eventMinute, 0, 0);
  return eventDate;
};

const parseTimeString = (time: string, settings: Settings, defaultOnNull: boolean = true): number => {

  if (!time) {
    if (!defaultOnNull) return NaN;
    time = getSODStartTime(settings);
  }
  const timeParts = time.split(":");
  if (timeParts.length !== 2) time = getSODStartTime(settings);
  else {
    if (timeParts[0].length === 1) time = timeParts[0] = `0${timeParts[0]}`;
    if (timeParts[1].length === 1) time = timeParts[1] = `0${timeParts[1]}`;
    time = `${timeParts[0]}:${timeParts[1]}`;
  }
  return Date.parse("1970-01-01T" + time + ":00Z");
};

export const getDateForDay = (day: EventDay): Date => {
  let date = new Date();
  for (let i = 0; i < 7; i += 1) {
    if (date.getDay() === convertToDateDay(day)) break;
    date = addDays(date, 1);
  }
  return date;
};
export function isWeekendDay(day: EventDay, weekendDays: EventDay[]): boolean {
  return weekendDays.indexOf(day) > -1;
}

export function convertToEventDay(s: string): EventDay {
  if (s === "Sunday") return EventDay.sunday;
  if (s === "Monday") return EventDay.monday;
  if (s === "Tuesday") return EventDay.tuesday;
  if (s === "Wednesday") return EventDay.wednesday;
  if (s === "Thursday") return EventDay.thursday;
  if (s === "Friday") return EventDay.friday;
  return EventDay.saturday;
}

export function convertToDateDay(s: EventDay): number {
  if (s === EventDay.monday) return 1;
  if (s === EventDay.tuesday) return 2;
  if (s === EventDay.wednesday) return 3;
  if (s === EventDay.thursday) return 4;
  if (s === EventDay.friday) return 5;
  if (s === EventDay.saturday) return 6;
  return 0;
}

export function convertToEventDayFromDate(s: JavascriptDay): EventDay {
  if (s === JavascriptDay.sunday) return EventDay.sunday;
  if (s === JavascriptDay.monday) return EventDay.monday;
  if (s === JavascriptDay.tuesday) return EventDay.tuesday;
  if (s === JavascriptDay.wednesday) return EventDay.wednesday;
  if (s === JavascriptDay.thursday) return EventDay.thursday;
  if (s === JavascriptDay.friday) return EventDay.friday;
  return EventDay.saturday;
}

export function convertDateToDay(e: JavascriptDay): string {
  switch (e) {
    case JavascriptDay.sunday:
      return "Sunday";
    case JavascriptDay.monday:
      return "Monday";
    case JavascriptDay.tuesday:
      return "Tuesday";
    case JavascriptDay.wednesday:
      return "Wednesday";
    case JavascriptDay.thursday:
      return "Thursday";
    case JavascriptDay.friday:
      return "Friday";
    default:
      return "Saturday";
  }
}

export function convertToDay(e: EventDay): string {
  switch (e) {
    case EventDay.sunday:
      return "Sunday";
    case EventDay.monday:
      return "Monday";
    case EventDay.tuesday:
      return "Tuesday";
    case EventDay.wednesday:
      return "Wednesday";
    case EventDay.thursday:
      return "Thursday";
    case EventDay.friday:
      return "Friday";
    default:
      return "Saturday";
  }
}

export function formatValidDate(
  inputDate: string | Date | number,
  dateFormat = "dd MMM yyyy HH:mm:ss"
): string {
  if (!inputDate) return "";
  const date = new Date(inputDate).getTime();
  if (isNaN(date)) return inputDate.toString();
  const formattedDate = format(new Date(inputDate), dateFormat);
  return formattedDate;
}

// this is called when queued credits are made or when the events are requested inthe ui
export const scheduleEvent = (
  settings: StatefulSettings,
  item: IEventItem,
  scheduleReason: string,
  now: Date,
  scheduleOptions?: IScheduleOptions
): ISchedulerTimerInfo | undefined => {
  const schedule = settings.getScheduleInfo();
  if (settings.hasEventScheduled(item.key))
    return schedule.find(
      (i) => i.key === item.key && i.itemType === ItemTypes.event
    );
  const time = getScheduleTime(
    settings,
    convertToEventDayFromDate(new Date().getDay()),
    item
  );
  const result = addEventCallback(
    settings,
    time,
    item,
    now,
    scheduleReason,
    scheduleOptions
  );
  return result;
};

// this is called when queued credits are made or when the events are requested inthe ui
export const scheduleTask = (
  settings: StatefulSettings,
  item: ITaskItem,
  now: Date,
  scheduleReason: string,
  scheduleOptions?: IScheduleOptions
): boolean => {
  if (settings.hasTaskScheduled(item.key)) return true;
  let time = now;
  const minTime = `${time.getHours()}:${time.getMinutes()}`;
  const maxTime = getLastScheduleOffsetTimeForDay(
    settings,
    convertToEventDayFromDate(now.getDay())
  );
  time = getRandomTime(settings, minTime, maxTime);
  const result = addTaskCallback(
    settings,
    time,
    item,
    now,
    scheduleReason,
    scheduleOptions
  );
  return result;
};

// this is called when queued credits are made or when the events are requested inthe ui
export const schedulePermission = (
  settings: StatefulSettings,
  showMessage: (args: IShowNotificationState) => void,
  item: IPermissionItem,
  ignoreMessage: boolean,
  now: Date,
  lowerDelay: number,
  upperDelay: number,
  scheduleReason: string,
  scheduleOptions?: IScheduleOptions
): boolean => {
  if (settings.hasPermissionScheduled(item.key)) return true;
  let time = now;
  let duration = 0;
  if (!item.maxTime) item.maxTime = getEndOfDayOffsetTime(settings);
  if (item.minTime || item.maxTime) {
    time = getScheduleTime(
      settings,
      convertToEventDayFromDate(new Date().getDay()),
      item
    );
    if (isTimedItem(item.countType)) {
      const permissionSettings = settings.getPermissionSettings();
      duration = getRandomCountForItem(
        item.count,
        permissionSettings.permissionDurationVariance,
        true
      );
    }
  }
  if (!ignoreMessage) {
    if (handlePermissionBySystem(settings)) {
      showMessage({
        message:
          "Your request has been sent. Your owner will respond when they are ready.",
        persist: true,
        notificationType: NotificationType.MessageSent,
      });
    } else
      showMessage({
        message: `Your request will be sent to ${settings.getControl().getOwner().username
          }.`,
        persist: true,
        notificationType: NotificationType.MessageSent,
      });
  }
  const result = addPermissionCallback(
    settings,
    time,
    duration,
    item,
    now,
    lowerDelay,
    upperDelay,
    scheduleReason,
    scheduleOptions
  );
  return result;
};

// handles the scheduling of events and finds time in the diary to fire it
const addPermissionCallback = (
  settings: StatefulSettings,
  startTime: Date,
  duration: number,
  item: IPermissionItem,
  now: Date,
  lowerDelay: number,
  upperDelay: number,
  scheduleReason: string,
  scheduleOptions?: IScheduleOptions
): boolean => {
  const skipIfPast = item.maxTime !== undefined;
  let workingDate = startTime;
  if (
    handlePermissionBySystem(settings) &&
    isSameMinute(workingDate, now) &&
    !scheduleOptions?.forceSchedule
  ) {
    // so we start immediately
    workingDate = addMinutes(workingDate, getRandom(lowerDelay, upperDelay)); // off set it so not so predicable
  }
  const eventStartTimeTicks = parseTimeString(
    getHours(workingDate) + ":" + getMinutes(workingDate),
    settings
  );
  let maxEndTimeTicks = 0;
  if (item.maxTime) maxEndTimeTicks = parseTimeString(item.maxTime, settings);
  if (
    skipIfPast &&
    !scheduleOptions?.forceSchedule &&
    item.maxTime &&
    maxEndTimeTicks < eventStartTimeTicks
  )
    return false; // if can skip and in past skip
  if (isBefore(workingDate, now)) workingDate = now; // if in past set to now
  const schedulerTime = differenceInMinutes(workingDate, startOfToday());
  const scheduledItem: ISchedulerTimerInfo = {
    ...item,
    startTime: addMinutes(startOfToday(), schedulerTime),
    duration,
    scheduleOptions,
    isPaused: true,
    isComplete: false,
    scheduleReason,
  };
  settings.updateSchedulerState(scheduledItem);
  settings.auditUser(
    new AuditMessage(
      AuditActionTypes.Permission,
      item.name,
      `Permission Scheduled for ${scheduledItem.startTime?.toLocaleString()}`,
      TraceLevel.info
    )
  );
  return true;
};

// handles the scheduling of events and finds time in the diary to fire it
const addEventCallback = (
  settings: StatefulSettings,
  startTime: Date,
  event: IEventItem,
  now: Date,
  scheduleReason: string,
  scheduleOptions?: IScheduleOptions
): ISchedulerTimerInfo | undefined => {
  const range = getScheduleTimeRange(settings, convertToEventDayFromDate(now.getDay()), event);
  const skipIfPast = range.maxDate !== undefined;
  let workingDate = startTime;
  const eventStartTimeTicks = parseTimeString(
    getHours(workingDate) + ":" + getMinutes(workingDate),
    settings
  );
  let maxEndTimeTicks = 0;
  // this needs to take into account the offset and the real 
  if (range.maxDate) maxEndTimeTicks = range.maxDate.getTime();
  if (
    skipIfPast &&
    !scheduleOptions?.forceSchedule &&
    range.maxDate &&
    maxEndTimeTicks < eventStartTimeTicks
  )
    return undefined; // if can skip and in past skip
  if (isBefore(workingDate, now)) workingDate = addMinutes(now, -5); // if in past set to 5 mins in the past so it will be picked up as missed
  const scheduledItem: ISchedulerTimerInfo = {
    ...event,
    startTime: workingDate,
    duration: 0,
    scheduleOptions,
    isPaused: true,
    isComplete: false,
    scheduleReason,
  };
  settings.updateSchedulerState(scheduledItem);
  settings.auditUser(
    new AuditMessage(
      AuditActionTypes.Event,
      event.name,
      `Event Scheduled for ${scheduledItem.startTime?.toLocaleString()}`,
      TraceLevel.info
    )
  );
  return scheduledItem;
};

// handles the scheduling of tasks and finds time in the diary to fire it
const addTaskCallback = (
  settings: StatefulSettings,
  startTime: Date,
  item: ITaskItem,
  now: Date,
  scheduleReason: string,
  scheduleOptions?: IScheduleOptions
): boolean => {
  let workingDate = startTime;
  if (isBefore(workingDate, now)) workingDate = now; // if in past set to now
  const scheduledItem = {
    ...item,
    startTime: workingDate,
    duration: item.credit,
    eventType: EventType.standard,
    scheduleOptions,
    isPaused: true,
    isComplete: false,
    scheduleReason,
    availability: AvailabilityType.random,
    approvalChance: 1,
    disableAdd: false,
    hasOptions: item.options && item.options.length > 0,
    isLimited: item.count && item.count > 0,
    optionAppliedChance: 1,
  } as ISchedulerTimerInfo;
  settings.updateSchedulerState(scheduledItem);
  settings.auditUser(
    new AuditMessage(
      AuditActionTypes.Task,
      item.name,
      `Task Scheduled for ${scheduledItem.startTime?.toLocaleString()}`,
      TraceLevel.info
    )
  );
  return true;
};

const handlePermissionBySystem = (settings: Settings): boolean => {
  // changed this to always send to owner regardless of setting if an owner is set.
  return settings.getControl().isOwnedBySelf();
};


const defaultStartTime = 4; // 4am
export function getDayHours(day: EventDay, overrideTimes: Record<EventDay, number[]>): number[] {
  if (overrideTimes && overrideTimes[day]) return overrideTimes[day];
  return [defaultStartTime, defaultStartTime + defaultDayLengthHours];
}